/**
 * Turn cost accounting for the header's Turn breakdown.
 *
 * The server sends, on every event that carries `session_cost_usd`, the itemised
 * cost ledger it pairs with (server/app/tse_agent/turn_cost_ledger.py):
 *   cost_seq   — the ledger seq the total is taken at (total == cost of lines <= seq)
 *   cost_lines — [{seq, label, kind: "llm", cost, ms?, in?, out?, cached?}, ...]
 * A turn is banked as the lines in (bankedSeq, latestSeq] and the session-cost
 * delta over the same window, so its rows always add up to its total; anything
 * the client could not itemise becomes an explicit "other / unitemised" row.
 *
 * Display: turn costs are apportioned (largest remainder) so the rounded turns
 * add up to the rounded session total, and each turn's rounded rows add up to
 * its rounded total — exact at the displayed precision.
 */

export const COST_DECIMALS = 4;
export const OTHER_ROW_LABEL = "other / unitemised";
export const OTHER_ROW_KIND = "other";
const COST_UNIT = 10 ** COST_DECIMALS;
// Below half a displayed unit an "other" row would print $0.0000; the remainder
// is still carried by the apportioned rows so nothing is hidden from the total.
const OTHER_MIN_USD = 0.5 / COST_UNIT;

const finite = (v) => Number.isFinite(Number(v));

export function createCostLedger() {
  return { latestCost: 0, bankedCost: 0, latestSeq: null, bankedSeq: 0, pending: new Map() };
}

/** Reset the ledger to a server baseline (join / new session). */
export function seedCostLedger(ledger, { cost = 0, bankedCost = cost, seq = null, bankedSeq = seq } = {}) {
  ledger.latestCost = Number(cost) || 0;
  ledger.bankedCost = Number(bankedCost) || 0;
  ledger.latestSeq = finite(seq) && seq !== null ? Number(seq) : null;
  ledger.bankedSeq = finite(bankedSeq) && bankedSeq !== null ? Number(bankedSeq) : 0;
  ledger.pending = new Map();
}

/** Fold one server payload's session_cost_usd / cost_seq / cost_lines into the ledger. */
export function absorbCostPayload(ledger, payload) {
  const cost = Number(payload?.session_cost_usd);
  if (!Number.isFinite(cost)) return false;
  (Array.isArray(payload?.cost_lines) ? payload.cost_lines : []).forEach((line) => {
    const seq = Number(line?.seq);
    if (Number.isFinite(seq) && seq > ledger.bankedSeq && !ledger.pending.has(seq)) ledger.pending.set(seq, line);
  });
  const seq = payload?.cost_seq == null ? NaN : Number(payload.cost_seq);
  if (Number.isFinite(seq)) {
    // Total and seq are one pair: take both from the newest snapshot.
    if (ledger.latestSeq === null || seq >= ledger.latestSeq) {
      ledger.latestSeq = seq;
      ledger.latestCost = Math.max(cost, ledger.bankedCost);
    }
  } else if (cost > ledger.latestCost) {
    ledger.latestCost = cost;
  }
  return true;
}

const lineCost = (row) => (finite(row?.cost) ? Number(row.cost) : 0);

/** A timing row (tool / retrieval / model time) never carries money once the server itemises lines. */
const isCosted = (row) => row?.cost != null && finite(row.cost);

/**
 * Turn rows: the timing steps interleaved with the cost lines (a step's `after`
 * is the ledger seq it followed), plus an "other" row for the unitemised rest.
 */
export function composeTurn(steps, lines, deltaUsd, itemised) {
  const delta = Math.max(0, Number(deltaUsd) || 0);
  const stepRows = Array.isArray(steps) ? steps : [];
  if (!itemised) {
    // A server without the ledger: the steps carry their own cost.
    const stepsCost = stepRows.reduce((sum, s) => sum + (isCosted(s) ? Number(s.cost) : 0), 0);
    const rows = [...stepRows];
    if (delta - stepsCost >= OTHER_MIN_USD) rows.push({ label: OTHER_ROW_LABEL, kind: OTHER_ROW_KIND, cost: delta - stepsCost });
    return { costUsd: Math.max(delta, stepsCost), steps: rows };
  }
  const timing = stepRows
    .filter((s) => !(s?.kind === "llm" && isCosted(s)))
    .map((s, i) => ({ row: (({ cost, ...rest }) => rest)(s), key: (finite(s?.after) ? Number(s.after) : -1) + 0.5, i }));
  const costed = (Array.isArray(lines) ? lines : []).map((l, i) => ({ row: l, key: Number(l.seq), i: i + timing.length }));
  const rows = [...timing, ...costed].sort((a, b) => (a.key - b.key) || (a.i - b.i)).map((x) => x.row);
  const itemisedCost = (Array.isArray(lines) ? lines : []).reduce((sum, l) => sum + lineCost(l), 0);
  if (delta - itemisedCost >= OTHER_MIN_USD) rows.push({ label: OTHER_ROW_LABEL, kind: OTHER_ROW_KIND, cost: delta - itemisedCost });
  return { costUsd: Math.max(delta, itemisedCost), steps: rows };
}

/** Bank the current turn: its cost lines, its cost and its rows. Mutates the ledger. */
export function bankTurnCost(ledger, steps) {
  const delta = Math.max(0, ledger.latestCost - ledger.bankedCost);
  ledger.bankedCost = Math.max(ledger.bankedCost, ledger.latestCost);
  const upto = ledger.latestSeq;
  if (upto === null) return composeTurn(steps, [], delta, false);
  const lines = [...ledger.pending.entries()]
    .filter(([seq]) => seq > ledger.bankedSeq && seq <= upto)
    .sort((a, b) => a[0] - b[0])
    .map(([seq, line]) => {
      ledger.pending.delete(seq);
      return line;
    });
  ledger.bankedSeq = Math.max(ledger.bankedSeq, upto);
  return composeTurn(steps, lines, delta, true);
}

/** Integer display units for `values`, summing exactly to round(total) units (largest remainder). */
export function apportionUnits(values, total) {
  const target = Math.round(Math.max(0, Number(total) || 0) * COST_UNIT);
  const raw = values.map((v) => Math.max(0, Number(v) || 0) * COST_UNIT);
  const units = raw.map(Math.floor);
  let rest = target - units.reduce((a, b) => a + b, 0);
  const byFraction = raw.map((r, i) => [r - Math.floor(r), i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (let k = 0; rest > 0 && byFraction.length; k += 1) {
    units[byFraction[k % byFraction.length][1]] += 1;
    rest -= 1;
  }
  while (rest < 0 && units.some((u) => u > 0)) {
    for (let k = byFraction.length - 1; rest < 0 && k >= 0; k -= 1) {
      const idx = byFraction[k][1];
      if (units[idx] > 0) {
        units[idx] -= 1;
        rest += 1;
      }
    }
  }
  return units;
}

export const formatUnits = (units) => `$${(units / COST_UNIT).toFixed(COST_DECIMALS)}`;

/**
 * Everything the panel shows, from the turn log alone: count, totals, averages,
 * and per-turn / per-row cost texts that add up at the displayed precision.
 */
export function summariseTurnLog(turnLog) {
  const turns = Array.isArray(turnLog) ? turnLog : [];
  const totalCostUsd = turns.reduce((sum, t) => sum + Math.max(0, Number(t.costUsd) || 0), 0);
  const totalLatencyMs = turns.reduce((sum, t) => sum + Math.max(0, Number(t.latencyMs) || 0), 0);
  const turnUnits = apportionUnits(turns.map((t) => t.costUsd), totalCostUsd);
  const rows = turns.map((t, ti) => {
    const steps = Array.isArray(t.steps) ? t.steps : [];
    const costedIdx = steps.map((s, i) => (isCosted(s) ? i : -1)).filter((i) => i >= 0);
    const stepUnits = apportionUnits(costedIdx.map((i) => steps[i].cost), turnUnits[ti] / COST_UNIT);
    const costTexts = new Map(costedIdx.map((i, k) => [i, formatUnits(stepUnits[k])]));
    return {
      ...t,
      costUnits: turnUnits[ti],
      costText: t.costUsd > 0 ? formatUnits(turnUnits[ti]) : "—",
      steps: steps.map((s, i) => (costTexts.has(i) ? { ...s, costText: costTexts.get(i) } : s)),
    };
  });
  const count = turns.length;
  const totalUnits = turnUnits.reduce((a, b) => a + b, 0);
  return {
    count,
    rows,
    totalCostUsd,
    totalLatencyMs,
    totalCostText: formatUnits(totalUnits),
    avgCostText: count ? `$${(totalUnits / COST_UNIT / count).toFixed(COST_DECIMALS)}` : "",
    avgLatencyS: count ? (totalLatencyMs / count / 1000).toFixed(1) : "",
  };
}
